import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Skeleton,
} from '@mui/material';
import {
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useContacts } from '../hooks/useContacts';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import ContactForm from '../components/ContactForm';
import { FormPage, FormActions } from '../../../components/common/form';

function serverPhonesToForm(phones) {
  return (phones || []).map((p, idx) => ({
    temp_id: p.phone_id || `phone-${idx}`,
    country_code: p.country_code,
    number: p.number,
    label: p.label || '',
  }));
}

function serverEmailsToForm(emails) {
  return (emails || []).map((e, idx) => ({
    temp_id: e.email_id || `email-${idx}`,
    email: e.email,
    label: e.label || '',
  }));
}

export default function EditContactPage() {
  const { t } = useTranslation(['contacts', 'common']);
  const { contactId } = useParams();
  const navigate = useNavigate();
  const { navigateBack } = useNavigationStack();
  const { showSuccess, showError } = useSnackbar();
  const { fetchContactById, updateContact, loading } = useContacts();

  const [formData, setFormData] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [fetching, setFetching] = useState(true);
  // Name as saved, for the breadcrumb (formData.name changes while typing)
  const [savedName, setSavedName] = useState(null);

  useEffect(() => {
    setFetching(true);
    fetchContactById(contactId).then((result) => {
      if (result.success) {
        const c = result.data.data;
        setSavedName(c.name);
        setFormData({
          name: c.name || '',
          att: c.att || '',
          contact_type: c.contact_type || 'company',
          role: c.role || '',
          phone_numbers: serverPhonesToForm(c.phone_numbers),
          emails: serverEmailsToForm(c.emails),
          address: c.address || null,
          website: c.website || '',
          cvr_number: c.cvr_number || '',
        });
      } else {
        showError(t('contacts:errors.load_failed'));
        navigate('/contacts');
      }
      setFetching(false);
    });
  }, [contactId]);

  const handleInputChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  }, [validationErrors]);

  const validate = useCallback(() => {
    const errors = {};
    if (!formData?.name?.trim()) errors.name = t('contacts:validation.name_required');
    return errors;
  }, [formData, t]);

  const buildPayload = useCallback(() => {
    return {
      name: formData.name.trim(),
      att: formData.att?.trim() || null,
      contact_type: formData.contact_type,
      role: formData.role?.trim() || null,
      phone_numbers: formData.phone_numbers
        .filter((p) => p.number?.trim())
        .map((p) => ({ country_code: p.country_code, number: p.number.trim(), label: p.label || null })),
      emails: formData.emails
        .filter((e) => e.email?.trim())
        .map((e) => ({ email: e.email.trim(), label: e.label || null })),
      address: formData.address && formData.address.address_line1?.trim() ? {
        address_line1: formData.address.address_line1.trim(),
        address_line2: formData.address.address_line2?.trim() || null,
        postal_code: formData.address.postal_code.trim(),
        city: formData.address.city.trim(),
        country: formData.address.country.trim(),
      } : null,
      website: formData.website?.trim() || null,
      cvr_number: formData.cvr_number?.trim() || null,
    };
  }, [formData]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    const result = await updateContact(contactId, buildPayload());
    if (result.success) {
      showSuccess(t('contacts:success.updated'));
      navigate(`/contacts/${contactId}`);
    } else {
      showError(result.error || t('contacts:errors.update_failed'));
    }
  }, [validate, updateContact, contactId, buildPayload, showSuccess, showError, navigate, t]);

  if (fetching) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={300} />
      </Box>
    );
  }

  return (
    <FormPage
      title={t('contacts:edit_title')}
      subtitle={formData.name || undefined}
      crumb={savedName}
      onBack={() => navigateBack(`/contacts/${contactId}`)}
      onSubmit={handleSubmit}
      fieldErrors={validationErrors}
    >
      <ContactForm
        formData={formData}
        onInputChange={handleInputChange}
        validationErrors={validationErrors}
        loading={loading}
      />
      <FormActions
        onCancel={() => navigateBack(`/contacts/${contactId}`)}
        submitType="submit"
        submitIcon={<SaveIcon />}
        loading={loading}
      />
    </FormPage>
  );
}
